import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import DashboardSidebar from '../../components/ui/DashboardSidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toAbsoluteApiMediaUrl } from '../../lib/mediaUrl';

const MyAccount = () => {
  const navigate = useNavigate();
  const { me, signOut, refresh } = useAuth();

  const user = useMemo(() => {
    const raw = String(me?.role || '').toLowerCase().trim();
    const role =
      raw === 'admin'
        ? ('admin' as const)
        : raw === 'profesor'
          ? ('teacher' as const)
          : raw === 'parinte'
            ? ('parent' as const)
            : raw === 'elev'
              ? ('student' as const)
              : raw === 'secretariat'
                ? ('secretariat' as const)
              : ('student' as const);

    return {
      role,
      name: me?.full_name || 'Utilizator',
      avatar: me?.avatar_url || undefined,
    };
  }, [me?.avatar_url, me?.full_name, me?.role]);

  const showSidebar = user.role === 'teacher' || user.role === 'admin' || user.role === 'secretariat';
  const isStudent = user.role === 'student';
  const isUnder16Student = isStudent && !Boolean((me as any)?.over16);
  const canManageOwnAvatar = !isStudent || Boolean((me as any)?.over16);

  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [newName, setNewName] = useState(me?.full_name || '');
  const [newPhone, setNewPhone] = useState(me?.phone || '');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  if (!me) {
    return <div className="min-h-screen bg-background" />;
  }

  const parseApiError = (err: any) => err?.response?.data?.detail || err?.message || 'Eroare.';

  const saveName = async () => {
    setInfo(null);
    setError(null);
    const name = newName.trim();
    if (!name) return setError('Numele este obligatoriu.');
    setBusy(true);
    try {
      const resp = await api.put('/api/users/me/name', { new_name: name });
      setInfo(resp.data?.message || 'Numele a fost actualizat.');
      await refresh();
    } catch (err: any) {
      setError(parseApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const savePhone = async () => {
    setInfo(null);
    setError(null);
    const phone = newPhone.trim();
    if (!phone) return setError('Completeaza numarul sau foloseste "Sterge telefon".');
    setBusy(true);
    try {
      const resp = await api.put('/api/users/me/phone', { new_phone: phone });
      setInfo(resp.data?.message || 'Telefon actualizat.');
      await refresh();
    } catch (err: any) {
      setError(parseApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const deletePhone = async () => {
    setInfo(null);
    setError(null);
    if (!window.confirm('Stergi numarul de telefon?')) return;
    setBusy(true);
    try {
      const resp = await api.delete('/api/users/me/phone');
      setInfo(resp.data?.message || 'Telefon sters.');
      setNewPhone('');
      await refresh();
    } catch (err: any) {
      setError(parseApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveAvatar = async () => {
    setInfo(null);
    setError(null);
    if (!avatarFile) return setError('Selecteaza o imagine.');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', avatarFile);
      await api.post('/api/users/me/avatar-file', fd);
      setInfo('Imagine actualizata.');
      setAvatarFile(null);
      await refresh();
    } catch (err: any) {
      setError(parseApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteAvatar = async () => {
    setInfo(null);
    setError(null);
    if (!window.confirm('Stergi imaginea de profil?')) return;
    setBusy(true);
    try {
      const resp = await api.delete('/api/users/me/avatar');
      setInfo(resp.data?.message || 'Imagine stearsa.');
      setAvatarFile(null);
      await refresh();
    } catch (err: any) {
      setError(parseApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async () => {
    setInfo(null);
    setError(null);
    if (!oldPassword.trim()) return setError('Completeaza parola veche.');
    if (!newPassword.trim()) return setError('Completeaza parola noua.');
    if (newPassword !== confirmPassword) return setError('Parolele noi nu coincid.');
    setBusy(true);
    try {
      const resp = await api.put('/api/users/me/password', {
        old_password: oldPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      setInfo(resp.data?.message || 'Parola a fost schimbata.');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(parseApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Contul meu • Scola</title>
      </Helmet>

      <Header
        user={user}
        notificationCount={0}
        onNavigate={(path) => navigate(path)}
        onSignOut={() => {
          signOut();
          navigate('/authentication-portal');
        }}
      />
      {showSidebar && <DashboardSidebar user={user} isCollapsed={false} onNavigate={(path) => navigate(path)} />}

      <main className={`pt-16 transition-all duration-300 ${showSidebar ? 'lg:pl-64' : ''}`}>
        <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Contul meu</h1>
              <p className="text-sm text-muted-foreground">Gestioneaza datele contului tau.</p>
            </div>
            <Button variant="outline" iconName="ArrowLeft" iconPosition="left" onClick={() => navigate(-1)}>
              Inapoi
            </Button>
          </div>

          {info && <div className="bg-success/10 border border-success/20 text-success rounded-md p-3 text-sm">{info}</div>}
          {error && <div className="bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{error}</div>}

          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <div className="flex items-center gap-4">
              {me.avatar_url ? (
                <img
                  src={toAbsoluteApiMediaUrl(me.avatar_url) || me.avatar_url}
                  alt="Profil"
                  className="w-16 h-16 rounded-full object-cover border border-border"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-semibold">
                  {(me.full_name || 'U').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium text-foreground truncate">{me.full_name}</div>
                <div className="text-xs text-muted-foreground truncate">{me.email}</div>
                <div className="text-xs text-muted-foreground truncate">{me.phone || '—'}</div>
                <div className="text-xs text-muted-foreground">{String(me.role || '')}</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {isStudent ? (isUnder16Student ? (
              <div className="bg-muted/30 border border-border rounded-lg p-6 space-y-2 lg:col-span-2">
                <div className="flex items-center gap-2">
                  <Icon name="Shield" size={18} className="text-muted-foreground" />
                  <h2 className="text-lg font-semibold text-foreground">Cont gestionat de părinți</h2>
                </div>
                <p className="text-sm text-muted-foreground">
                  Pentru elevii sub 16 ani, parola și imaginea de profil sunt gestionate din contul părintelui.
                </p>
              </div>
            ) : (
              <div className="bg-muted/30 border border-border rounded-lg p-6 space-y-2 lg:col-span-2">
                <div className="flex items-center gap-2">
                  <Icon name="Info" size={18} className="text-muted-foreground" />
                  <h2 className="text-lg font-semibold text-foreground">Date personale</h2>
                </div>
                <p className="text-sm text-muted-foreground">
                  Datele elevului (nume, telefon, etc.) sunt gestionate de profesor. Aici poți schimba doar parola.
                </p>
              </div>
            )) : (
              <div className="bg-card border border-border rounded-lg p-6 space-y-3">
                <div className="flex items-center gap-2">
                  <Icon name="User" size={18} className="text-muted-foreground" />
                  <h2 className="text-lg font-semibold text-foreground">Profil</h2>
                </div>
                <Input label="Nume" value={newName} onChange={(e) => setNewName(e.target.value)} disabled={busy} />
                <div className="flex items-center justify-end gap-2">
                  <Button variant="default" onClick={saveName} disabled={busy}>
                    Salveaza numele
                  </Button>
                </div>
              </div>
            )}

            {!isStudent && (
              <div className="bg-card border border-border rounded-lg p-6 space-y-3">
                <div className="flex items-center gap-2">
                  <Icon name="Phone" size={18} className="text-muted-foreground" />
                  <h2 className="text-lg font-semibold text-foreground">Contact</h2>
                </div>
                <Input label="Telefon" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} disabled={busy} />
                <div className="flex items-center justify-end gap-2">
                  <Button variant="outline" onClick={deletePhone} disabled={busy || !me.phone}>
                    Sterge telefon
                  </Button>
                  <Button variant="default" onClick={savePhone} disabled={busy}>
                    Salveaza telefon
                  </Button>
                </div>
              </div>
            )}

            {canManageOwnAvatar && (
              <div className="bg-card border border-border rounded-lg p-6 space-y-3">
                <div className="flex items-center gap-2">
                  <Icon name="Image" size={18} className="text-muted-foreground" />
                  <h2 className="text-lg font-semibold text-foreground">Imagine profil</h2>
                </div>
                <label className="block text-sm font-medium text-foreground">
                  Incarca fisier
                  <input
                    type="file"
                    accept="image/*"
                    className="mt-2 block w-full text-sm text-muted-foreground"
                    disabled={busy}
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      try {
                        setAvatarFile(f);
                      } catch (err: any) {
                        setError(String(err?.message || 'Nu s-a putut incarca fisierul.'));
                      }
                    }}
                  />
                </label>
                <div className="flex items-center justify-end gap-2">
                  <Button variant="outline" onClick={deleteAvatar} disabled={busy || !me.avatar_url}>
                    Sterge imagine
                  </Button>
                  <Button variant="default" onClick={saveAvatar} disabled={busy || !avatarFile}>
                    Salveaza imagine
                  </Button>
                </div>
              </div>
            )}

            {!isUnder16Student ? (
              <div className="bg-card border border-border rounded-lg p-6 space-y-3">
              <div className="flex items-center gap-2">
                <Icon name="Key" size={18} className="text-muted-foreground" />
                <h2 className="text-lg font-semibold text-foreground">Schimba parola</h2>
              </div>
              <Input label="Parola veche" type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} disabled={busy} />
              <Input label="Parola noua" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} disabled={busy} />
              <Input
                label="Confirma parola noua"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={busy}
              />
              <div className="flex items-center justify-end gap-2">
                <Button variant="default" onClick={changePassword} disabled={busy}>
                  Schimba parola
                </Button>
              </div>
              <div className="text-xs text-muted-foreground">
                Dupa schimbarea parolei, e posibil sa fie nevoie sa te reconectezi.
              </div>
              </div>
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
};

export default MyAccount;
